/**
 * Contenido narrativo del INTERIOR DE LA CASA — la escena importante del
 * juego (ver `docs/biblia/escenarios.md`, sección "EL INTERIOR DE LA CASA").
 * TODO el texto vive aquí como dato: la escena `InteriorCasaScene` solo lo
 * renderiza, no inventa frases. Esto mantiene la separación dura
 * motor/contenido que pide el encargo.
 *
 * Convenciones:
 * - Tono callejero de colegas, humor GTA, sin nombres reales — solo apodos.
 * - Solo apodos de la biblia de personajes (`docs/biblia/personajes.md`).
 * - Las charlas que mencionan a los "no salen" (AB G, HASBEEN KICKED,
 *   CHANCLEX) los traen a presencia por mención, canon.
 */

import type { DialogueLine } from './types';

/** id estable de una acción disponible en el interior de la casa. */
export type InteriorActionId =
  | 'sentarse'
  | 'charlar'
  | 'fumar'
  | 'ver-videos'
  | 'jugar-tablet';

/** Una acción elegible desde la botonera de InteriorCasaScene. */
export interface InteriorAction {
  id: InteriorActionId;
  nombre: string;
  emoji: string;
  descripcion: string;
}

/** Botonera de acciones disponibles en el interior. El orden es el que se
 *  renderiza, de arriba a abajo. */
export const INTERIOR_ACTIONS: InteriorAction[] = [
  {
    id: 'sentarse',
    nombre: 'Sentarte',
    emoji: '🪑',
    descripcion: 'La mesa grande, junto al fuego. Sitio de sobra.',
  },
  {
    id: 'charlar',
    nombre: 'Charlar con tu colega',
    emoji: '💬',
    descripcion: 'El que esté esta noche. Siempre tiene tema.',
  },
  {
    id: 'fumar',
    nombre: 'Fumar',
    emoji: '🚬',
    descripcion: 'Una de calada, mirando al fuego.',
  },
  {
    id: 'ver-videos',
    nombre: 'Ver vídeos en la tablet',
    emoji: '📱',
    descripcion: 'YouTube random, vídeos de coches y memes.',
  },
  {
    id: 'jugar-tablet',
    nombre: 'Jugar en la tablet',
    emoji: '🎮',
    descripcion: 'Algún casual o emulador.',
  },
];

/** Sticker canon del SDOWP — `docs/biblia/personajes.md`: el poder narrativo
 *  del protagonista es el sticker que convoca al grupo. Texto EXACTO. */
export const STICKER_REUNION_TEXTO = 'jo up casa tu que haces guapa?';

/** id canon de la misión que arranca mandando este sticker. */
export const MISION_REUNION_ID = 'la-casa-random';

// -----------------------------------------------------------------------
// Frases de ambiente (sentarse / fumar / ver vídeos).
// Cada acción ambient elige una al azar al abrir el panel.
// -----------------------------------------------------------------------

/** Frases cortas para la acción "Sentarte". 2-3 variantes, callejeras. */
export const FRASES_SENTARSE: string[] = [
  'Te sientas en la mesa grande. La madera está caliente todavía del que fuchicó antes.',
  'La silla cruje. Aquí han cenado los mismos doce mil veces — y se nota.',
  'Te sientas mirando al fuego. Nadie dice nada un rato. Está bien.',
];

/** Frases cortas para la acción "Fumar". 2-3 variantes. */
export const FRASES_FUMAR: string[] = [
  'Calentada de las buenas. El humo se va para el techo y vuelve.',
  'Le das una calada larga. La brasa brilla un segundo y se apaga.',
  'Otra calada. Afuera se oye el monte. Aquí dentro solo el crepitar del fuego.',
];

/** Frases cortas para "Ver vídeos en la tablet". 2-3 variantes. */
export const FRASES_VER_VIDEOS: string[] = [
  'YouTube te pone un vídeo de coches. Otro de coches. Otro más de coches.',
  'Acabas en un gameplay random. No sabes cómo has llegado aquí.',
  'Te sale un vídeo de hace ocho años. Te quedas cinco minutos igual.',
];

/** Frase fija para "Jugar en la tablet": no hay pool porque es un único
 *  estado terminal (jugando), sin progresión narrativa. */
export const FRASE_JUGAR_TABLET =
  'Te pones con el emulador. La batería de la tablet dura lo que dura.';

/** Elige una frase al azar del pool correspondiente. Helper para la escena. */
export function fraseAccion(id: InteriorActionId): string {
  switch (id) {
    case 'sentarse':
      return FRASES_SENTARSE[Math.floor(Math.random() * FRASES_SENTARSE.length)];
    case 'fumar':
      return FRASES_FUMAR[Math.floor(Math.random() * FRASES_FUMAR.length)];
    case 'ver-videos':
      return FRASES_VER_VIDEOS[Math.floor(Math.random() * FRASES_VER_VIDEOS.length)];
    case 'jugar-tablet':
      return FRASE_JUGAR_TABLET;
    // charlar no usa este helper: tiene su propio pool de charlas.
    case 'charlar':
      return '';
  }
}

// -----------------------------------------------------------------------
// Charlas: cada una con un colega distinto. Avance por tap como
// MissionScene (retrato + apodo + texto + indicador ▼).
// -----------------------------------------------------------------------

/** Una charla completa con un colega concreto. */
export interface Charla {
  /** id estable para analytics / debug. */
  id: string;
  colegaId: string;
  /** Texto del título que se muestra al abrir la charla (ej. "YOUNG EMO"). */
  titulo: string;
  /** Líneas de diálogo en orden. Avance por tap. */
  lineas: DialogueLine[];
}

/** Pool de charlas: cada colega tiene su rollo. Al abrir "charlar" se elige
 *  una al azar y se avanza línea a línea con tap (mismo patrón que el
 *  MissionScene). Los personajes referenciados pero que no salen aparecen
 *  mencionados en alguna línea — canon de presencia por mención. */
export const CHARLAS: Charla[] = [
  {
    id: 'charla-young-emo',
    colegaId: 'young-emo',
    titulo: 'YOUNG EMO — heavy y videojuegos',
    lineas: [
      {
        personajeId: 'young-emo',
        emocion: 'neutral',
        texto: 'Tío, ¿has visto el directo que hice ayer? Grabé un playthrough del Bloodborne y acabé llorando de risa.',
      },
      {
        personajeId: 'young-emo',
        emocion: 'neutral',
        texto: 'Luego nos liamos hablando de cómo el bajo en el metal negro tiene que sonar a “olla exprés mal afinada”. Y mola.',
      },
      {
        personajeId: 'young-emo',
        emocion: 'neutral',
        texto: 'Oye, que el HASBEEN KICKED me pasó una foto de no sé dónde y adivinó que era “un parking de un Mercadona a las 14:37 de un martes”. Te juro que es brujo.',
      },
    ],
  },
  {
    id: 'charla-moai',
    colegaId: 'moai',
    titulo: 'MOAI — insultos con cariño',
    lineas: [
      {
        personajeId: 'moai',
        emocion: 'neutral',
        texto: 'Anda ya, pedazo de inútil, siéntate aquí que no estorbes.',
      },
      {
        personajeId: 'moai',
        emocion: 'neutral',
        texto: 'He traído melocotones del almacén. Si los quieres, píllate uno. Si no, te los comes igual, so vaguete.',
      },
      {
        personajeId: 'moai',
        emocion: 'neutral',
        texto: 'A las seis en la casa como un reloj, ¿eh? Como siempre. Ni un día tarde, a diferencia de ti, payaso.',
      },
      {
        personajeId: 'moai',
        emocion: 'neutral',
        texto: 'Hoy me han dicho que el AB G está metiendo triples de espaldas desde otro país. El tío es un animal.',
      },
    ],
  },
  {
    id: 'charla-banagrayn',
    colegaId: 'banagrayn',
    titulo: 'BANAGRAYN — pocas palabras, graciosas',
    lineas: [
      {
        personajeId: 'banagrayn',
        emocion: 'neutral',
        texto: '…',
      },
      {
        personajeId: 'banagrayn',
        emocion: 'neutral',
        texto: 'He visto un corzo.',
      },
      {
        personajeId: 'banagrayn',
        emocion: 'neutral',
        texto: 'El corzo me ha visto a mí. Nos hemos quedado mirándonos un rato largo.',
      },
      {
        personajeId: 'banagrayn',
        emocion: 'neutral',
        texto: 'Se ha ido él primero. Cabrón.',
      },
    ],
  },
  {
    id: 'charla-sempai',
    colegaId: 'sempai',
    titulo: 'SEMPAI — tímido, casino y vicios',
    lineas: [
      {
        personajeId: 'sempai',
        emocion: 'neutral',
        texto: 'Oye… ¿fumas?',
      },
      {
        personajeId: 'sempai',
        emocion: 'neutral',
        texto: 'Es que… me he apostado los diez pavos del taxi a una mano de blackjack online y los he perdido.',
      },
      {
        personajeId: 'sempai',
        emocion: 'neutral',
        texto: 'No le digas a nadie que te he pedido uno, ¿vale?… Es que me he quedado sin recargas y aquí estás tú.',
      },
    ],
  },
  {
    id: 'charla-xano',
    colegaId: 'xano',
    titulo: 'XANO — te cuenta cosas',
    lineas: [
      {
        personajeId: 'xano',
        emocion: 'neutral',
        texto: 'Oye, ¿sabías que han sacado un modelo nuevo de IA que te hace trading de cripto en piloto automático?',
      },
      {
        personajeId: 'xano',
        emocion: 'neutral',
        texto: 'Yo lo he estado probando en demo y la verdad es que asusta. No pierde. Igual lo monto en real la semana que viene, a ver qué pasa.',
      },
      {
        personajeId: 'xano',
        emocion: 'neutral',
        texto: 'Ayer el CHANCLEX me pasó un paper sobre modelos de suspensión de F1. Te juro que lo entendí a la primera. El tío explica bien.',
      },
      {
        personajeId: 'xano',
        emocion: 'neutral',
        texto: 'Si algún día me junto con él, monto un fondo cuantitativo con su punto de vista de ingeniero. Pero eso son castillos en el aire.',
      },
    ],
  },
];

/** Elige una charla al azar del pool. */
export function elegirCharla(): Charla {
  return CHARLAS[Math.floor(Math.random() * CHARLAS.length)];
}
